"use client";

import React, { useState, useCallback } from "react";
import { db } from "../lib/firebase";
import { collection, addDoc } from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { userLogger } from "../lib/userLogger";
import Image from "next/image";
import { AiOutlineSearch } from "react-icons/ai";
import { X } from "lucide-react";
import { useApplicationState } from "../contexts/ApplicationContext";
import { formatArtistOwnersLabel } from "@/lib/owners";

export interface ArtistSearchResult {
  id: string;
  name: string;
  type?: string;
  image?: string;
  owner?: string;
  owners?: { [key: string]: { name?: string } };
  inviteStatus?: "pending" | "accepted";
  city?: string;
  state?: string;
  genre?: string;
}

interface ArtistSearchProps {
  onArtistSelected: (artist: ArtistSearchResult) => void;
  onNewArtistCreated?: (artist: ArtistSearchResult) => void;
  placeholder?: string;
  label?: string;
  eventType?: string;
  excludeArtistIds?: string[];
  autoFocus?: boolean;
  className?: string;
  restrictToVenueArtists?: boolean;
}

export default function ArtistSearch({
  onArtistSelected,
  onNewArtistCreated,
  placeholder = "Search for an artist",
  label = "Search Artist",
  eventType = "Artist",
  excludeArtistIds = [],
  autoFocus = false,
  className = "",
  restrictToVenueArtists = false,
}: ArtistSearchProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const [foundArtists, setFoundArtists] = useState<ArtistSearchResult[]>([]);
  const [exactMatchArtist, setExactMatchArtist] = useState<ArtistSearchResult | null>(null);
  const [isSearching, setIsSearching] = useState(false);

  const { searchArtists, searchVenueArtistsWithExactMatch } = useApplicationState();
  const auth = getAuth();

  const formatName = (raw?: string) => {
    if (!raw) return "";
    return raw
      .toString()
      .split(/\s+/)
      .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1) : ""))
      .join(" ");
  };

  const normalizeImageSrc = (src?: string) => {
    if (!src) return "/img/missing-image.png";
    if (
      src.startsWith("http://") ||
      src.startsWith("https://") ||
      src.startsWith("/")
    )
      return src;
    if (src.includes("/")) return src.startsWith("/") ? src : `/${src}`;
    return `/img/${src}`;
  };

  const handleSearch = useCallback(async (term: string) => {
    if (!term.trim()) {
      setFoundArtists([]);
      setExactMatchArtist(null);
      return;
    }

    setIsSearching(true);
    try {
      if (restrictToVenueArtists) {
        // Use restricted search (venue artists only + exact match from database)
        const { venueArtists, exactMatch } = await searchVenueArtistsWithExactMatch(term);
        // Filter out excluded artists
        const filtered = venueArtists.filter(
          (artist) => !excludeArtistIds.includes(artist.id)
        );
        setFoundArtists(filtered);
        setExactMatchArtist(exactMatch && !excludeArtistIds.includes(exactMatch.id) ? exactMatch : null);
      } else {
        // Use full search (all artists)
        const results = await searchArtists(term);
        // Filter out excluded artists
        const filtered = results.filter(
          (artist) => !excludeArtistIds.includes(artist.id)
        );
        setFoundArtists(filtered);
        setExactMatchArtist(null);
      }
    } catch (error) {
      console.error("Error searching artists:", error);
    } finally {
      setIsSearching(false);
    }
  }, [searchArtists, searchVenueArtistsWithExactMatch, excludeArtistIds, restrictToVenueArtists]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    handleSearch(value);
  };

  const createNewArtist = async () => {
    if (!searchTerm.trim()) return;

    try {
      const auth = getAuth();
      const uid = auth.currentUser?.uid;
      // ⚠️ `type` is deliberately NOT set. It holds a category SLUG from
      // lib/genres.ts (music | performing_arts | comedy | other), and this
      // component's `eventType` is a display label defaulting to the literal
      // "Artist" — writing it here put a non-slug in the field, so the artist
      // editor showed no category and an empty genre list. Absent means
      // unset, for the artist or an organizer to choose on the profile.
      const newArtist = {
        name: searchTerm.trim(),
        createdBy: uid,
        createdAt: new Date(),
      };

      const artistRef = await addDoc(collection(db, "performers"), newArtist);

      try {
        await userLogger.logProfileCreated(artistRef.id, newArtist.name);
      } catch (logError) {
        console.warn("[Logger Warning] Failed to audit trail profile creation:", logError);
      }

      const artistWithId: ArtistSearchResult = {
        id: artistRef.id,
        name: newArtist.name,
        // The display label, which is all `eventType` ever was. It is handed
        // to the caller so the row reads the same as every other result, and
        // is deliberately not what got stored — see above.
        type: eventType,
      };

      setSearchTerm("");
      setFoundArtists([]);
      setExactMatchArtist(null);

      if (onNewArtistCreated) {
        onNewArtistCreated(artistWithId);
      } else {
        onArtistSelected(artistWithId);
      }
    } catch (error) {
      console.error("Error creating artist:", error);
    }
  };

  const selectArtist = (artist: ArtistSearchResult) => {
    onArtistSelected(artist);
    setSearchTerm("");
    setFoundArtists([]);
  };


  const clearSearch = () => {
    setSearchTerm("");
    setFoundArtists([]);
    setExactMatchArtist(null);
    const el = document.getElementById("artist-search-input") as HTMLInputElement | null;
    if (el) el.focus();
  };

  return (
    <div className={`${className}`}>
      {label && (
        <div className="search-container">
          <label className="p block font-small mb-1">{label}</label>
        </div>
      )}

      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-bold-700">
          <AiOutlineSearch size={35} />
        </span>
        <input
          id="artist-search-input"
          type="text"
          value={searchTerm}
          onChange={handleSearchChange}
          placeholder={placeholder}
          autoFocus={autoFocus}
          className="pl-12 pr-12 py-3 bg-gray-100 border-none rounded-lg text-base font-medium placeholder-black focus:outline-none focus:ring-2 focus:ring-teal-500 focus:bg-teal-50 w-full"
        />
        {searchTerm && (
          <button
            type="button"
            onClick={clearSearch}
            aria-label="Clear search"
            className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center rounded-full"
            style={{ background: "var(--active-green)" }}
          >
            <X className="w-3 h-3 text-white" />
          </button>
        )}
      </div>

      {(foundArtists.length > 0 || exactMatchArtist || searchTerm) && (
        <div className="mt-3 max-h-[300px] overflow-y-auto space-y-2">
          {isSearching && (
            <div className="bg-white rounded-lg p-3 text-center text-gray-500">
              Searching...
            </div>
          )}

          {/* Show exact match from database if found */}
          {!isSearching && exactMatchArtist && (
            <div
              key={exactMatchArtist.id}
              onClick={() => selectArtist(exactMatchArtist)}
              className="bg-white rounded-lg p-3 hover:bg-[var(--gold)] transition-colors cursor-pointer border-2 border-blue-400"
            >
              <div className="flex items-center gap-3">
                <div className="flex-shrink-0">
                  {exactMatchArtist.image ? (
                    <Image
                      src={normalizeImageSrc(exactMatchArtist.image)}
                      alt={exactMatchArtist.name}
                      width={44}
                      height={44}
                      className="rounded-lg object-cover"
                    />
                  ) : (
                    <div className="w-[44px] h-[44px] bg-gray-200 rounded-lg flex items-center justify-center">
                      <Image
                        src="/img/missing-image.png"
                        alt="add artist image"
                        width={28}
                        height={28}
                      />
                    </div>
                  )}
                </div>

                <div className="flex-1 flex items-center min-w-0">
                  <p className="truncate m-0">
                    <span className="font-semibold truncate m-0 capitalize first-letter:uppercase">
                      {formatName(exactMatchArtist.name)}
                    </span>
                    <span className="px-2">|</span>
                    <span className="font-normal capitalize first-letter:uppercase">
                      {exactMatchArtist.type || eventType}
                    </span>
                    <span className="ml-2 text-xs text-blue-600 font-medium">
                      (Exact match)
                    </span>
                  </p>
                </div>
              </div>
            </div>
          )}

          {!isSearching && foundArtists.map((artist) => {
            const ownerText = formatArtistOwnersLabel(artist.owners, auth.currentUser?.uid);

            return (
              <div
                key={artist.id}
                onClick={() => selectArtist(artist)}
                className="bg-white rounded-lg p-3 hover:bg-[var(--gold)] transition-colors cursor-pointer"
              >
                <div className="flex items-center gap-3">
                  <div className="flex-shrink-0">
                    {artist.image ? (
                      <Image
                        src={normalizeImageSrc(artist.image)}
                        alt={artist.name}
                        width={44}
                        height={44}
                        className="rounded-lg object-cover"
                      />
                    ) : (
                      <div className="w-[44px] h-[44px] bg-gray-200 rounded-lg flex items-center justify-center">
                        <Image
                          src="/img/missing-image.png"
                          alt="add artist image"
                          width={28}
                          height={28}
                        />
                      </div>
                    )}
                  </div>

                  <div className="flex-1 flex items-center min-w-0">
                    <p className="truncate m-0">
                      <span className="font-semibold truncate m-0 capitalize first-letter:uppercase">
                        {formatName(artist.name)}
                      </span>
                      <span className="px-2">|</span>
                      <span className="font-normal capitalize first-letter:uppercase">
                        {artist.type || eventType}
                      </span>
                    </p>
                  </div>

                  {ownerText && (
                    <div className="flex-1 flex items-center justify-end min-w-0 pr-5">
                      <p className="text-xs text-gray-600 font-normal truncate m-0">
                        {ownerText}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            );
          })}

          {/* Always show "add new artist" option when there's a search term */}
          {!isSearching && searchTerm && (
            <div
              className="bg-white rounded-lg border-none p-3 hover:bg-[var(--gold)] cursor-pointer transition-colors"
              onClick={createNewArtist}
            >
              <div className="flex items-center gap-3">
                <div className="flex-shrink-0">
                  <div className="w-[44px] h-[44px] bg-gray-200 rounded-lg flex items-center justify-center">
                    <Image
                      src="/img/missing-image.png"
                      alt="add artist image"
                      width={28}
                      height={28}
                    />
                  </div>
                </div>
                <p className="truncate m-0">
                  <span className="font-semibold">{searchTerm}</span>
                  <span className="text-gray-500 ml-2">
                    {exactMatchArtist
                      ? "– Add another artist with same name"
                      : "– Click to add new artist"}
                  </span>
                </p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}